import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type {
  ScriptType,
  ContactListDto,
  VoicemailDto,
  WizardStep,
  SessionsTabState,
  SessionContactStatus,
} from '../types/wizard.types';
import { OngoingCallAction, DEFAULT_SESSIONS_TAB_STATE } from '../types/wizard.types';
import { type ContactsTab } from '@features/contacts';

interface OutreachData {
  outreachSequenceId: string;
  outreachSequenceStep: string;
  nonce: string;
}

export type StepMode = 'select' | 'create';

interface WizardState {
  currentStep: WizardStep;
  contactsCount: number;
  selectedIntent: ScriptType | null;
  selectedContactList: ContactListDto | null;
  contactListId: string;
  currentContactsTab: ContactsTab;
  selectedVoicemail: VoicemailDto | null;
  /**
   * Setter specifically named for currentContactsTab.  Exists alongside
   * setContactsTab for clarity/consumers that reference the field name directly.
   */
  setCurrentContactsTab: (tab: ContactsTab) => void;
  shuffleContacts: boolean;
  ongoingCallAction: OngoingCallAction;
  isEditingFromSummary: boolean;
  selectedFolder: string;
  salesforceNonce: string;
  outreachData: OutreachData;
  hubspotNonce: string;
  hubspotPortalId: number;
  salesforceURL: string;
  stepMode: Record<number, StepMode>;

  sessionsTab: SessionsTabState;
  setSelectedSessionIds: (ids: string[]) => void;
  toggleSelectedSessionId: (id: string) => void;
  setSessionsActiveFilters: (filters: SessionContactStatus[]) => void;
  toggleSessionsFilter: (status: SessionContactStatus) => void;
  setNewListName: (name: string) => void;
  setSessionsSearchQuery: (query: string) => void;
  setSessionsCurrentPage: (page: number) => void;
  setSessionsPageSize: (size: number) => void;
  resetSessionsTab: () => void;

  setStep: (step: WizardStep) => void;
  setIntent: (intent: ScriptType | null) => void;
  setContactsCount: (count: number) => void;
  setContactList: (list: ContactListDto | Array<{ contactListId: string; completed: boolean }> | null) => void;
  setContactListId: (id: string) => void;
  setContactsTab: (tab: ContactsTab) => void;
  setVoicemail: (vm: VoicemailDto | null) => void;
  setShuffleContacts: (value: boolean) => void;
  setOngoingCallAction: (action: OngoingCallAction) => void;
  setIsEditingFromSummary: (value: boolean) => void;
  setSelectedFolder: (value: string) => void;
  setSalesforceNonce: (value: string) => void;
  setOutreachData: (value: OutreachData) => void;
  setHubspotNonce: (value: string) => void;
  setHubspotPortalId: (value: number) => void;
  setSalesforceURL: (value: string) => void;
  setStepMode: (step: number, mode: StepMode) => void;
  nextStep: () => void;
  prevStep: () => void;
  goToSummary: () => void;
  reset: () => void;
  wizardResetInitialState: () => void;
  canProceed: () => boolean;
  isSessionStarted: boolean;
  setIsSessionStarted: (active: boolean) => void;
}

const defaultStepMode: Record<number, StepMode> = { 1: 'select', 2: 'select', 3: 'select' };

const initialState = {
  currentStep: 1 as WizardStep, 
  contactsCount: 0,
  selectedIntent: null,
  selectedContactList: null as ContactListDto | null,
  contactListId: '',
  currentContactsTab: 'csv' as ContactsTab,
  selectedVoicemail: null,
  shuffleContacts: false,
  ongoingCallAction: OngoingCallAction.CUT_OFF,
  isEditingFromSummary: false,
  selectedFolder: '',
  salesforceNonce: '',
  outreachData: { outreachSequenceId: '', outreachSequenceStep: '', nonce: '' } satisfies OutreachData,
  hubspotNonce: '',
  hubspotPortalId: '',
  salesforceURL: '',
  isSessionStarted: false,
  stepMode: { ...defaultStepMode },
  sessionsTab: { ...DEFAULT_SESSIONS_TAB_STATE },
};

const useWizardStore = create<WizardState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setStep: (step) => {
        set({ currentStep: step });
      },

      setSelectedFolder: (folderName) => {
        set({ selectedFolder: folderName });
      },

      setSalesforceNonce: (nonce) => {
        set({ salesforceNonce: nonce });
      },

      setSalesforceURL: (url: string) => {
        set({ salesforceURL: url });
      },

      setHubspotNonce: (nonce) => {
        set({ hubspotNonce: nonce });
      },

      setHubspotPortalId: (portalId) => {
        set({ hubspotPortalId: portalId });
      },

      setIntent: (intent) => {
        set({ selectedIntent: intent });
      },

      setContactsCount: (count) => {
        set({ contactsCount: count });
      },

      setContactList: (list) => {
        set({ selectedContactList: list });
      },

      setContactListId: (id: string) => {
        set({ contactListId: id });
      },

      setContactsTab: (tab) => {
        set({ currentContactsTab: tab });
      },

      setCurrentContactsTab: (tab) => {
        set({ currentContactsTab: tab });
      },

      setVoicemail: (vm) => {
        set({ selectedVoicemail: vm });
      },

      setShuffleContacts: (value) => {
        set({ shuffleContacts: value });
      },

      setOngoingCallAction: (action) => {
        set({ ongoingCallAction: action });
      },

      setIsSessionStarted: (active: boolean) => {
        set({ isSessionStarted: active });
      },

      setOutreachData: (outreachData) => {
        set({ outreachData });
      },

      setStepMode: (step, mode) => {
        set((state) => ({
          stepMode: { ...state.stepMode, [step]: mode },
        }));
      },

      setIsEditingFromSummary: (value) => {
        set({ isEditingFromSummary: value });
      },

      setSelectedSessionIds: (ids) => {
        set((state) => ({
          sessionsTab: { ...state.sessionsTab, selectedSessionIds: ids, currentPage: 1 },
        }));
      },

      toggleSelectedSessionId: (id) => {
        set((state) => {
          const current = state.sessionsTab.selectedSessionIds;
          const next = current.includes(id)
            ? current.filter((existingId) => existingId !== id)
            : [...current, id];
          return {
            sessionsTab: { ...state.sessionsTab, selectedSessionIds: next, currentPage: 1 },
          };
        });
      },

      setSessionsActiveFilters: (filters) => {
        set((state) => ({
          sessionsTab: { ...state.sessionsTab, activeFilters: filters, currentPage: 1 },
        }));
      },

      toggleSessionsFilter: (status) => {
        set((state) => {
          const current = state.sessionsTab.activeFilters;
          const next = current.includes(status)
            ? current.filter((existingStatus) => existingStatus !== status)
            : [...current, status];
          return {
            sessionsTab: { ...state.sessionsTab, activeFilters: next, currentPage: 1 },
          };
        });
      },

      setNewListName: (name) => {
        set((state) => ({
          sessionsTab: { ...state.sessionsTab, newListName: name },
        }));
      },

      setSessionsSearchQuery: (query) => {
        set((state) => ({
          sessionsTab: { ...state.sessionsTab, searchQuery: query },
        }));
      },

      setSessionsCurrentPage: (page) => {
        set((state) => ({
          sessionsTab: { ...state.sessionsTab, currentPage: page },
        }));
      },

      setSessionsPageSize: (size) => {
        set((state) => ({
          sessionsTab: { ...state.sessionsTab, pageSize: size, currentPage: 1 },
        }));
      },

      resetSessionsTab: () => {
        set({ sessionsTab: { ...DEFAULT_SESSIONS_TAB_STATE } });
      },

      nextStep: () => {
        const { currentStep } = get();
        if (currentStep < 5) {
          set({ currentStep: (currentStep + 1) as WizardStep });
        }
      },

      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 1) {
          set({ currentStep: (currentStep - 1) as WizardStep });
        }
      },

      goToSummary: () => {
        set({ currentStep: 5, isEditingFromSummary: false });
      },

      reset: () => {
        set((state) => ({
          ...initialState,
          stepMode: { ...defaultStepMode },
          contactListId: state.contactListId,
          contactsCount: 0,
          currentContactsTab: state.currentContactsTab,
          outreachData: state.outreachData,
          salesforceNonce: state.salesforceNonce,
          hubspotNonce: state.hubspotNonce,
          hubspotPortalId: state.hubspotPortalId,
          isSessionStarted: state.isSessionStarted,
          salesforceURL: state.salesforceURL,
          sessionsTab: state.sessionsTab,
        }));
      },

      wizardResetInitialState: () => {
        set({ ...initialState, stepMode: { ...defaultStepMode } });
      },

      canProceed: () => {
        const { currentStep, selectedIntent, selectedContactList, selectedVoicemail } = get();
        switch (currentStep) {
        case 1:
          return selectedIntent !== null;
        case 2:
          return selectedContactList !== null;
        case 3:
          return selectedVoicemail !== null;
        case 4:
          // Session Settings step - always can proceed (shuffle and cutoff have defaults)
          return true;
        case 5:
          return selectedIntent !== null && selectedContactList !== null && selectedVoicemail !== null;
        default:
          return false;
        }
      },
    }),
    {
      name: 'wizard-session-storage',
      version: 2,
      partialize: (state) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { stepMode, ...rest } = state;
        return rest;
      },
      migrate: (persistedState, version) => {
        if (version === 2 && persistedState != null) {
          const { id,  } = persistedState as ContactListDto;
          const { contactListId } = persistedState as { contactListId: string; completed: boolean };
            
          return {
            ...persistedState,
            contactListId:
              id ?? contactListId ?? '',
          } as unknown as WizardState;
        }
        return persistedState  as WizardState;
      },
    },
  ),
);

export default useWizardStore;